import { DecoratorNode,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { useCallback, useEffect } from 'react';
import { mergeRegister } from '@lexical/utils';

export class PageBreakNode extends DecoratorNode {
  static getType() {
    return 'page-break';
  }

  static clone(node) {
    return new PageBreakNode(node.__key);
  }

  static importJSON() {
    return $createPageBreakNode();
  }

  static importDOM() {
    return {
      figure: (domNode) => {
        const tp = domNode.getAttribute('type');
        if (tp !== 'page-break') return null;
        return {
          conversion: () => ({ node: $createPageBreakNode() }),
          priority: 2,
        };
      },
    };
  }

  exportJSON() {
    return {
      type: this.getType(),
      version: 1,
    };
  }

  createDOM() {
    const el = document.createElement('figure');
    el.style.pageBreakAfter = 'always';
    el.setAttribute('type', this.getType());
    return el;
  }

  getTextContent() {
    return '\n';
  }

  isInline() {
    return false;
  }

  updateDOM() {
    return false;
  }

  exportDOM() {
    const el = document.createElement('figure');
    el.setAttribute('type', this.getType());
    el.style.pageBreakAfter = 'always';
    return { element: el };
  }

  decorate() {
    return <PageBreakComponent nodeKey={this.__key} />;
  }
}

function PageBreakComponent({ nodeKey }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);

  const onDelete = useCallback(
    (event) => {
      event.preventDefault();
      if (isSelected && $isNodeSelection($getSelection())) {
        const node = $getNodeByKey(nodeKey);
        if (node) {
          node.remove();
          return true;
        }
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSelected, nodeKey]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const pbElem = editor.getElementByKey(nodeKey);
          if (event.target === pbElem || pbElem?.contains(event.target)) {
            if (!event.shiftKey) clearSelection();
            setSelected(!isSelected);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSelection, editor, isSelected, nodeKey, onDelete, setSelected]);

  useEffect(() => {
    const pbElem = editor.getElementByKey(nodeKey);
    if (pbElem !== null) {
      pbElem.className = isSelected
        ? 'editor-page-break selected'
        : 'editor-page-break';
    }
  }, [editor, isSelected, nodeKey]);

  return null;
}

export function $createPageBreakNode() {
  return new PageBreakNode();
}

export function $isPageBreakNode(node) {
  return node instanceof PageBreakNode;
}
